//================================================================
//  Component: Manage Approver Reassign
//================================================================

//  Purpose: Sequence of approval management pages for Mobile view

//  Properties:
//    - approver = {Object, contains approval details}

//  Example:
//  <ManageApproverReassign 
//    approver={approver}
//  ></ManageApproverReassign> 

//================================================================


//Libraries
import React, { useState, useContext } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';

//Contexts
import { GetUser } from '../../Library/GlobalContexts';

//Components
import UserSearch from '../UserSearch/UserSearch';

//Functions
import WriteDocument from '../../Library/WriteDocument';

//Images
import ArrowIcon from '../../Components/Images/Icon_LeftArrow_Black.svg';
import TickIcon from '../../Components/Images/Icon_Approved_Green.svg';
import ErrorIcon from '../../Components/Images/Icon_Error_Red.svg'


export default function ManageApproverReassign({
  approver
}) {

  //------------------------------------------------------
  //  useContexts
  //------------------------------------------------------

  const getUser = useContext(GetUser);

  //------------------------------------------------------
  //  useStates
  //------------------------------------------------------

  // Stages for different pages > 'pre-reassign', 'reassign', 'post-reassign', 'error'
  const [stage, setStage] = useState('pre-reassign');

  // Used to show loading spinner when submitting form
  const [loading, setLoading] = useState(false);

  //------------------------------------------------------
  //  React Hook Form
  //------------------------------------------------------

  // Used for form validation
  const {
    register,
    handleSubmit,
    reset,
    clearErrors,
    setError,
    setValue,
    watch,
    formState: { errors },
  } = useForm();

  // Watch the required fields to determine if form is complete
  const watchedApprover = watch('approver');
  const watchedJustification = watch('justification');

  // Check if form is ready to submit
  const isFormValid = watchedApprover && watchedJustification && !Object.keys(errors).length;

  //------------------------------------------------------
  //  Functions
  //------------------------------------------------------

  // Submit Handler
  const onSubmit = (data) => {

    setLoading(true);

    const docPromises = [];

    // Create Document ID using role, email, etc.
    const documentId = `${approver?.approvalbucket.toLowerCase().replace(/\s+/g, '')}-${approver?.role.toLowerCase().replace(/\s+/g, '')}-${approver?.approveremail}-${Date.now()}`;

    const updatedApprover = {
      approveremail: data?.approver,
      approvergivenname: data?.approvergivenname,
      approversurname: data?.approversurname,
      lastmodifieddate: new Date(),
      lastmodifiedby: {
        email: getUser?.emailaddress,
        givenname: getUser?.givenname,
        surname: getUser?.surname,
      },
    };

    // Assign approver to the role
    docPromises.push(
      WriteDocument('approvers', approver?.approverid, updatedApprover, true),
    );

    const approverHistoryDoc = {
      itemid: documentId,
      previousapprover: {
        email: approver?.approveremail,
        givenname: approver?.approvergivenname,
        surname: approver?.approversurname,
      },
      newapprover: {
        email: data?.approver,
        givenname: data?.approvergivenname,
        surname: data?.approversurname,
      },
      actionedby: {
        email: getUser?.emailaddress,
        givenname: getUser?.givenname,
        surname: getUser?.surname,
      },
      actioneddate: new Date(),
      justification: data?.justification || '',
      role: approver?.role,
      approvalbucket: approver?.approvalbucket,
    };

    docPromises.push(
      WriteDocument('approverhistory', approverHistoryDoc.itemid, approverHistoryDoc),
    );

    return Promise.all(docPromises)
      .then(() => {
        reset();
        setLoading(false);
        setStage('post-reassign');
      })
      .catch((error) => {
        console.log('error', error);
        reset();
        setLoading(false);
        setStage('error');

      });

  };

  //------------------------------------------------------
  //  HTML
  //------------------------------------------------------

  return (
    <>

      {/* =========================================================== */}
      {/*  PRE-REASSIGN STAGE: ONLY SHOW BUTTON                       */}
      {/* =========================================================== */}

      <div className='px-4 py-2'>
        <button
          className='Secondary-Button w-full my-0'
          onClick={() => {
            setStage('reassign');
          }}
        >
          Reassign
        </button>
      </div>

      {/* =========================================================== */}
      {/*  REASSIGN STAGE                                             */}
      {/* =========================================================== */}

      <div className='absolute flex flex-col h-[calc(100vh-70px)] w-full left-0 top-[70px] z-10 bg-white' hidden={stage !== 'reassign'}>

        <form className='flex flex-col w-full' onSubmit={handleSubmit(onSubmit)}>

          {/* Header */}
          <div className='flex flex-row p-3 border-b-[1px] border-neutral-300'>

            {/* Back: Returns to previous screen, does not clear text field */}
            <img src={ArrowIcon} alt='Back' className='h-6' onClick={() => { setStage('pre-reassign') }}></img>
            <h5 className='m-auto'>Reassign Approver</h5>

          </div>

          {/* Body */}
          <div className='h-[calc(100vh-207px)] flex flex-col overflow-y-auto p-3 border-solid border'>

            {/* Approver */}
            <div className='Form-Row'>
              <label className='font-medium'>Approver</label>
              <input
                className='Input-Field-Text w-full'
                value={approver?.approveremail}
                type='text'
                disabled
              ></input>
            </div>

            {/* Role */}
            <div className='Form-Row'>
              <label className='font-medium'>Role</label>
              <input
                className='Input-Field-Text w-full'
                value={approver?.role}
                type='text'
                disabled
              ></input>
            </div>

            {/* Reassign request to */}
            <div className='Form-Row'>

              <label className='font-medium'>
                Reassign request to: <span className='text-[#C4314B]'>*</span>
              </label>

              <div className='w-full'>
                <UserSearch
                  dataField={'approver'}
                  required={true}
                  register={register}
                  clearErrors={clearErrors}
                  setError={setError}
                  setValue={setValue}
                  errors={errors.approver}
                ></UserSearch>
              </div>

            </div>

            {/* Justification */}
            <div className='Form-Row'>

              <label className='font-medium'>
                Justification: <span className='text-[#C4314B]'>*</span>
              </label>

              <textarea
                className={errors.justification ? 'Input-Field-TextArea-Error' : 'Input-Field-TextArea'}
                {...register('justification', { required: 'Justification is required' })}
                placeholder='Enter justification'
              ></textarea>

              {/* Error Message */}
              {
                errors.justification &&
                <span className='font-medium text-sm text-[#DE0000]'>
                  {errors.justification.message}
                </span>
              }
            </div>

          </div>

          {/* Footer */}
          <div className='px-2 py-3'>

            <div className='px-4 flex flex-row gap-3 justify-center'>

              {/* Submit Button */}
              <button className='Primary-Button whitespace-nowrap w-[150px]' disabled={loading || !isFormValid} type='submit' value='submit'>
                Submit
              </button>

              {/* Cancel Button */}
              <button
                className='Secondary-Button whitespace-nowrap w-[150px]'
                onClick={() => {
                  reset();
                  setStage('pre-reassign');
                }}
                type='button'
              >
                Cancel
              </button>

            </div>

          </div>

        </form>
      </div>

      {/* =========================================================== */}
      {/* POST-REASSIGN STAGE: SHOW SUCCESS TOAST                     */}
      {/* =========================================================== */}

      <div className='absolute flex flex-col justify-between w-full h-1/4 bg-white shadow-lg z-10 left-0 bottom-0 p-3' hidden={stage !== 'post-reassign'}>

        <div className='flex flex-row gap-2 p-3 mb-0 bg-[#e7f2da] rounded-md'>
          <img src={TickIcon} alt='Success'></img>
          <label className='font-semibold'>Reassigned successfully</label>
        </div>

        <div className='Submit-Button' onClick={() => setStage('pre-reassign')}>
          Close
        </div>

      </div>

      {/* =========================================================== */}
      {/* ERROR: SHOW FAILURE TOAST                                   */}
      {/* =========================================================== */}

      <div className='absolute flex flex-col justify-between w-full h-[170px]  bg-white shadow-lg z-10 left-0 bottom-0 p-3' hidden={stage !== 'error'}>

        <div className='flex flex-row gap-2 p-3 py-2 w-full bg-[#FFEBEE] rounded-md items-center justify-center'>
          <img className='w-[25px] h-[25px]' src={ErrorIcon} alt='Error'></img>
          <label>Something went wrong. Please try again or <Link className='underline' target='_blank' to='/help' onClick={() => setStage('pre-reassign')}>get help</Link>.</label>
        </div>

        <div className='Submit-Button w-full' onClick={() => setStage('pre-reassign')}>
          Close
        </div>

      </div>

    </>

  )
}
